<template>
  <div
    class="products-list"
    :class="listClasses"
    v-if="elementsList"
    role="list"
    :aria-label="productListStore?.currentCategory?.name"
  >
    <div
      v-if="isLoading"
      class="products-list__loader loader"
      role="progressbar"
    />
    <template
      v-for="(element, index) in elementsList.elements"
      :key="`${index}-${getId(element)}`"
    >
      <div
        role="listitem"
        v-if="hasInsertionRow(index)"
        class="products-list__element products-list__element--type_insertion"
      >
        <slot
          v-if="useInitialSlots && slotExists(`slot-insertion-${insertionIndex(index)}`)"
          :name="`slot-insertion-${insertionIndex(index)}`"
        />
        <div
          v-else-if="!useInitialSlots"
          class="slot-insertion teaser-in-column grid"
        >
          <dynamic-template
            :html="getInsertionHtml(index)"
          />
        </div>
      </div>
      <div
        v-if="element.type === 'product' || element.type === 'product-wishlist'"
        role="listitem"
        :aria-label="element.data.name"
        :class="{
          [`products-list__element products-list__element--type_${element.type}`]: true,
        }"
      >
        <component
          :is="getProductBoxComponent(element.data)"
          class="products-list__product-box"
          :product-box-data="element.data"
          :hide-counter-button="false"
          :state="productStates.get(element.data.itemId)"
          :tracking-additional="{
            position: index,
            list: trackingList
          }"
          :lazy-load="true"
          @addToBasket="addToBasket"
          @addToWishlist="onAddToWishlist"
          @removeFromWishlist="onRemoveFromWishlist"
          @quantityChange="onQuantityChange"
        />
      </div>
    </template>
  </div>
  <div
    class="products-list__no-products"
    v-else
    v-html="$t('productlist.no_products')"
  />
</template>

<script lang="ts">
  import {defineComponent, ref, computed, watch, onMounted, PropType, useSlots} from 'vue';
  import breakpoints from '../../variables/js/breakpoints';
  import ProductListItem from '../../types/ProductListItem';
  import ProductBoxData from '../../types/ProductBoxData';
  import type ElementsList from '../../types/ElementsList';
  import productListStore from '../../stores/product/ProductListStore';
  import productsListService from '../../services/productsList/ProductsListService';
  import StateManager from '../../utilities/js/stateManager/StateManager';
  import ProductBox from '../productBox/productBox.vue';
  import ProductBoxWide from '../productBoxWide/productBoxWide.vue';
  import ProductBoxCampaign from '../productBoxCampaign/productBoxCampaign.vue';
  import ProductBoxSet from '../productBoxSet/productBoxSet.vue';
  import DynamicTemplate from '../dynamicTemplate/dynamicTemplate.vue';
  import {usePlaceholder} from '../../utilities/vueMixins/placeholderMixin/placeholder';
  import pigeon from '../../utilities/js/pigeon/pigeon';
  import {throttle} from '@/bps/utilities/js/throttle/throttle';

  export default defineComponent({
    name: 'ProductsList',

    components: {
      ProductBox,
      ProductBoxWide,
      ProductBoxCampaign,
      ProductBoxSet,
      DynamicTemplate
    },

    props: {
      trackingList: {
        type: String,
        default: 'product-list'
      },
      listType: {
        type: String as PropType<'GRID' | 'LIST' | 'SET' | 'CAMPAIGN'>,
        default: 'GRID'
      },
      maxListColumns: {
        type: Number,
        default: 3
      }
    },

    setup(props) {
      const slots = useSlots();
      const productStates = ref(new StateManager());
      const elementsList = ref<ElementsList>({elements: [], insertions: []});
      const insertionModulo = ref<number>(window.innerWidth >= 1580 && props.maxListColumns === 4 ? 8 : 6);
      let isMobile: boolean = null;
      let isLoading = ref(false);
      let useInitialSlots = ref(true);

      const getId = (obj: ProductListItem) => {
        if (obj.data) {
          return obj.data.id + '_' + obj.data.itemId;
        }
        return obj.teaserId;
      };

      const insertionIndex = (index: number) => {
        return Math.floor(index / insertionModulo.value);
      };

      const hasInsertionRow = (index: number) => {
        if (index % insertionModulo.value === 0 || index === 0) {
          let insertionIndex = Math.floor(index / insertionModulo.value);
          if (useInitialSlots.value) {
            return slotExists(`slot-insertion-${insertionIndex}`);
          } else {
            const insertion = elementsList.value.insertions.find((element) => element.slot === insertionIndex);
            return insertion?.teaserHtml && insertion?.teaserHtml !== '';
          }
        }
        return false;
      };

      const getInsertionHtml = (index: number) => {
        const insertionIndex = Math.floor(index / insertionModulo.value);
        const insertion = elementsList.value.insertions.find((element) => element.slot === insertionIndex);
        if (insertion) {
          return insertion.teaserHtml;
        }
        return '';
      };

      const getInsertion = (element: ProductListItem) => {
        return productsListService.getInsertion(productListStore.currentCategory.id, element.teaserId)
          .then((response) => {
            if (!response || response === '') {
              return '';
            }
            return response;
          });
      };

      const listTemplate = computed(() => {
        if (props.listType === 'GRID') {
          return 'grid';
        } else if (props.listType === 'LIST') {
          return isMobile ? 'grid' : 'list';
        } else if (props.listType === 'CAMPAIGN') {
          return 'campaign';
        } else {
          return 'list';
        }
      });

      const getProductBoxComponent = (product: ProductBoxData) => {
        if (listTemplate.value === 'list' && product.articlesInSet.length) {
          return 'ProductBoxSet';
        } else if (listTemplate.value === 'list' && !isMobile) {
          return 'ProductBoxWide';
        } else if (listTemplate.value === 'campaign') {
          return 'ProductBoxCampaign';
        } else {
          return 'ProductBox';
        }
      };

      const listClasses = computed(() => {
        const classes = {};
        classes['products-list--loading'] = isLoading.value;
        classes[`products-list--type_${listTemplate.value}`] = true;
        return classes;
      });

      const slotExists = (name: string) => {
        return !!slots[name];
      };

      const breakpointChangeHandler = () => {
        isMobile = window.innerWidth < breakpoints.tablet;
        let columns = window.innerWidth >= 1580 && props.maxListColumns === 4 ? 8 : 6;
        if (insertionModulo.value !== columns) {
          insertionModulo.value = columns;
        }
      };

      const addToBasket = (product: ProductBoxData, quantity: number) => {
        productStates.value.set(product.itemId, StateManager.STATE_LOAD_BASKET);
        productsListService.addToBasket(product, quantity, props.trackingList)
          .then(() => {
            productStates.value.remove(product.itemId);
          }).catch(() => {
            productStates.value.set(product.itemId, StateManager.STATE_ERROR);
          });
      };

      const onAddToWishlist = (product: ProductBoxData) => {
        productStates.value.set(product.itemId, StateManager.STATE_LOAD_WISHLIST);
        productsListService.addToWishlist(product.id, product.itemId, props.trackingList)
          .then(() => {
            productStates.value.remove(product.itemId);
          }).catch(() => {
            productStates.value.set(product.itemId, StateManager.STATE_ERROR);
          });
      };

      const onRemoveFromWishlist = (product: ProductBoxData) => {
        productStates.value.set(product.itemId, StateManager.STATE_LOAD_WISHLIST);
        productsListService.removeFromWishlist(product.id, product.itemId, props.trackingList)
          .then(() => {
            productStates.value.remove(product.itemId);
          })
          .catch(() => {
            productStates.value.set(product.itemId, StateManager.STATE_ERROR);
          });
      };

      const onQuantityChange = (product: ProductBoxData, quantity: number) => {
        productsListService.changeQuantity(product.id, product.itemId, quantity);
      };

      watch(() => productListStore.elementsList, async (newList) => {
        const insertionsWithoutHtml = newList.insertions.some(insertion => insertion.teaserHtml === undefined);
        if (insertionsWithoutHtml) {
          const newInsertionList = await Promise.all(newList.insertions.map(async (insertion) => {
            let cloneInsertion = {...insertion};
            if (!insertion.teaserHtml) {
              cloneInsertion.teaserHtml = await getInsertion(insertion);
            }
            return cloneInsertion;
          }));
          useInitialSlots.value = false;
          elementsList.value.insertions = newInsertionList;
          const updatedElementsList = {...newList, insertions: newInsertionList};
          elementsList.value.elements = updatedElementsList.elements;
        } else {
          useInitialSlots.value = false;
          elementsList.value.insertions = newList.insertions;
          elementsList.value.elements = newList.elements;
        }
      }, {deep: true});

      onMounted(async () => {
        elementsList.value = productListStore.elementsList;
        usePlaceholder('products-list');
        window.addEventListener('resize', throttle(breakpointChangeHandler, 50));
        pigeon.subscribe('productList:loadStart', () => isLoading.value = true);
        pigeon.subscribe('productList:loadComplete', () => isLoading.value = false);
        breakpointChangeHandler();
      });

      return {
        elementsList,
        listClasses,
        getProductBoxComponent,
        getId,
        addToBasket,
        onAddToWishlist,
        onRemoveFromWishlist,
        onQuantityChange,
        slotExists,
        isLoading,
        productStates,
        productListStore,
        useInitialSlots,
        insertionModulo,
        hasInsertionRow,
        insertionIndex,
        getInsertionHtml
      };
    }
  });
</script>
